import {
  faArrowDownToLine,
  faArrowUpFromLine,
  faLocationDot,
  faMobileNotch,
  faPlus,
  faTowerBroadcast,
  faTrash,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parseAsFloat, parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { useTripContext } from '@/contexts/TripContext';
import { TripRow } from '@/design-system/composite/TripRow';
import { Button } from '@/design-system/primitives/Button';
import { fireConfetti } from '@/design-system/primitives/Confetti';
import { MenuRow } from '@/design-system/primitives/MenuRow';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';
import { TextInput } from '@/design-system/primitives/TextInput';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { twx } from '@/utils/tailwind';

export interface TripSelectorContentProps {
  /** Called after the user has performed an action that should close the menu. */
  onAction: () => void;
  /** Opens the iOS Add-to-Home-Screen instructions modal. Provided by
   *  the parent (App) because the menu popover closes on action — the
   *  modal needs to live outside the popover stacking context. Skipped
   *  on platforms where the native install prompt is available; that
   *  path is taken inline via `useInstallPrompt`. */
  onShowInstallInstructions?: () => void;
}

const today = (): string => new Date().toISOString().slice(0, 10);
const defaultEndDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
};

export const TripSelectorContent = ({
  onAction,
  onShowInstallInstructions,
}: TripSelectorContentProps) => {
  const { trips, selectedTripId, selectTrip, createTrip, importTrip, exportTrip, deleteTrip } =
    useTripContext();
  const installState = useInstallPrompt();
  // Show the install row when we either have a native prompt to fire
  // (Android / Chrome / Edge / desktop PWA-capable browsers) OR when
  // we're on iOS where the user must do it manually. Hidden once the
  // app is already running standalone.
  const showInstallRow =
    !installState.isInstalled && (installState.canInstall || installState.isIOS);
  const handleInstallClick = () => {
    if (installState.canInstall) {
      // Native browsers (Chrome / Edge / Android) → fire the OS install
      // dialog directly. Closing the menu first keeps the popover from
      // sitting open behind the OS dialog.
      onAction();
      void installState.install();
    } else if (installState.isIOS) {
      // iOS Safari → defer to the parent to open the instructions modal.
      onAction();
      onShowInstallInstructions?.();
    }
  };
  const [locationName] = useQueryState('q', parseAsString.withDefault(''));
  const [currentLat] = useQueryState('lat', parseAsFloat);
  const [currentLng] = useQueryState('lng', parseAsFloat);
  // Captured onto the new trip so selecting it later restores the same radius
  // that warmed the area's cache during exploration.
  const [currentRadius] = useQueryState('radius', parseAsInteger.withDefault(10));

  const [showNewTripForm, setShowNewTripForm] = useState(false);
  const [newTripName, setNewTripName] = useState('');
  const [newTripStart, setNewTripStart] = useState(today);
  const [newTripEnd, setNewTripEnd] = useState(defaultEndDate);
  // Trip currently in "are you sure?" state for deletion. Only one row at a
  // time — clicking another row's delete cancels the previous confirmation.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showNewTripForm) nameInputRef.current?.focus();
  }, [showNewTripForm]);

  const handleSelectTrip = (tripId: string) => {
    selectTrip(tripId);
    onAction();
  };

  // Bridge to the Collaborate modal (rendered in App.tsx) via a window event.
  // The modal MUST live outside this component: the popover around it dismisses
  // on outside-click (mousedown), so a click inside a portaled modal would
  // otherwise unmount this whole tree before the modal's click handler runs.
  const handleCollaborateOpen = (tripId: string) => {
    window.dispatchEvent(new CustomEvent('trippy-open-collaborate-modal', { detail: { tripId } }));
  };

  const handleCreate = () => {
    if (!newTripName.trim()) return;
    const end = newTripEnd >= newTripStart ? newTripEnd : newTripStart;
    // First-ever trip on this device — celebrate with confetti. localStorage
    // sentinel ensures the burst fires exactly once per user, not on every
    // trip after the first.
    const FIRST_TRIP_KEY = 'trip-explorer:first-trip-celebrated';
    const isFirstTrip = trips.length === 0 && !localStorage.getItem(FIRST_TRIP_KEY);
    const trip = createTrip(
      newTripName.trim(),
      locationName,
      newTripStart,
      end,
      currentLat,
      currentLng,
      currentRadius,
    );
    if (isFirstTrip) {
      localStorage.setItem(FIRST_TRIP_KEY, '1');
      // Defer a tick so the new trip's UI mounts before the confetti
      // overlay — otherwise the burst can fire against the still-mounted
      // form and feel disconnected from the trip-row that just appeared.
      window.setTimeout(() => fireConfetti(), 50);
    }
    selectTrip(trip.id);
    setNewTripName('');
    setShowNewTripForm(false);
    onAction();
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data: unknown = JSON.parse(ev.target?.result as string);
        const result = importTrip(data);
        if (!result) window.alert("Could not import — make sure it's a valid trip export.");
        else {
          selectTrip(result.id);
          onAction();
        }
      } catch {
        window.alert('Could not read that file.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Hidden file input lives at the top so its ref is stable across the
  // form-open / chip-cluster swap below.
  const importInput = (
    <input
      ref={importInputRef}
      type="file"
      accept=".json"
      onChange={handleImportFile}
      className="hidden"
    />
  );

  if (showNewTripForm) {
    return (
      <>
        {importInput}
        <div className="p-3 space-y-2">
          <p className="text-xs font-medium text-content-subtle">New trip</p>
          <TextInput
            ref={nameInputRef}
            type="text"
            size="sm"
            value={newTripName}
            onChange={(e) => setNewTripName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') {
                setShowNewTripForm(false);
                setNewTripName('');
              }
            }}
            placeholder="My Summer Adventure"
          />
          <div className="grid grid-cols-2 gap-2">
            <TextInput
              type="date"
              value={newTripStart}
              onChange={(e) => setNewTripStart(e.target.value)}
            />
            <TextInput
              type="date"
              value={newTripEnd}
              min={newTripStart}
              onChange={(e) => setNewTripEnd(e.target.value)}
            />
          </div>
          {locationName && (
            <p className="text-xs text-content-subtle flex items-center gap-1">
              <FontAwesomeIcon icon={faLocationDot} style={{ width: 10, height: 10 }} />
              Location: {locationName}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              fullWidth
              className="flex-1"
              onClick={handleCreate}
              disabled={!newTripName.trim()}
            >
              Create trip
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowNewTripForm(false);
                setNewTripName('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Default view: trips listed vertically with full meta (date, location,
  // place count). "New trip" and "Import" sit below as compact one-line rows
  // sharing the same hover treatment, so the whole stack reads as a uniform
  // set of clickable rows — no dividers needed.
  return (
    <>
      {importInput}
      {/* Outer container uses space-y-1 for tighter rhythm between the trips
          card and the New/Import buttons, instead of the loose space-y-2 it
          had before. */}
      <div className="p-2 space-y-1">
        {trips.length > 0 && (
          <div className="border border-content-border rounded-xl overflow-hidden">
            <div className="p-2 space-y-0.5">
              <SectionEyebrow className="px-2 pt-1 pb-1">Trips</SectionEyebrow>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {trips.map((trip) => {
                  // When pending delete, replace the whole row with a
                  // dedicated confirmation surface — trying to fit "Delete?
                  // Delete Cancel" alongside the trip metadata was too
                  // cramped on narrow menus.
                  if (pendingDeleteId === trip.id) {
                    return (
                      <div
                        key={trip.id}
                        className="rounded-lg overflow-hidden bg-error-background border border-error-border px-3 py-2.5 space-y-2"
                      >
                        <p className="text-xs text-error-text leading-snug">
                          Delete{' '}
                          <span className="font-semibold text-content-text">{trip.name}</span>?
                          <span className="block text-content-subtle">
                            This removes your local copy only.
                          </span>
                        </p>
                        <div className="flex items-center gap-1.5">
                          <Button
                            size="sm"
                            variant="destructive"
                            fullWidth
                            className="flex-1"
                            onClick={() => {
                              deleteTrip(trip.id);
                              setPendingDeleteId(null);
                            }}
                            aria-label={`Confirm delete ${trip.name}`}
                          >
                            Delete
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            fullWidth
                            className="flex-1"
                            onClick={() => setPendingDeleteId(null)}
                            aria-label="Cancel delete"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    );
                  }
                  // Active (pinging) once the trip actually has collaborators.
                  const collabActive = (trip.collaborators?.length ?? 0) > 0;
                  // The Collaborate button (broadcast tower). Opens the single
                  // Collaborate modal: add people by email, manage the list, and
                  // copy the share link.
                  const collabButton = (
                    <button
                      key="collab"
                      type="button"
                      onClick={() => handleCollaborateOpen(trip.id)}
                      aria-label={`Collaborate on ${trip.name}`}
                      title={
                        collabActive
                          ? 'Collaborators · manage & share'
                          : 'Collaborate — add people and share'
                      }
                      className={twx(
                        'relative w-7 h-7 rounded-md flex items-center justify-center transition-colors',
                        collabActive
                          ? 'text-quaternary-emphasized bg-quaternary-subtle hover:bg-quaternary-subtle/70'
                          : 'text-content-subtle hover:text-quaternary-emphasized hover:bg-white/50',
                      )}
                    >
                      {collabActive && (
                        <span
                          aria-hidden
                          className="absolute inset-1 rounded-full bg-quaternary-main/40 motion-safe:animate-ping pointer-events-none"
                        />
                      )}
                      <FontAwesomeIcon
                        icon={faTowerBroadcast}
                        style={{ width: 11, height: 11 }}
                        className={twx('relative', collabActive ? '' : 'opacity-60')}
                      />
                    </button>
                  );
                  const exportButton = (
                    <button
                      key="export"
                      type="button"
                      onClick={() => exportTrip(trip.id)}
                      aria-label={`Export ${trip.name}`}
                      title="Export trip as JSON"
                      className="w-7 h-7 rounded-md flex items-center justify-center text-content-subtle hover:text-secondary-emphasized hover:bg-white/50 transition-colors"
                    >
                      <FontAwesomeIcon icon={faArrowDownToLine} style={{ width: 12, height: 12 }} />
                    </button>
                  );
                  const deleteButton = (
                    <button
                      key="delete"
                      type="button"
                      onClick={() => setPendingDeleteId(trip.id)}
                      aria-label={`Delete ${trip.name}`}
                      title="Delete trip (local only)"
                      className="w-7 h-7 rounded-md flex items-center justify-center text-content-subtle hover:text-error-main hover:bg-white/50 transition-colors"
                    >
                      <FontAwesomeIcon icon={faTrash} style={{ width: 11, height: 11 }} />
                    </button>
                  );
                  // 2-column grid: [Collaborate][Delete] / [Export].
                  const actionGrid = [collabButton, deleteButton, exportButton];
                  return (
                    <TripRow
                      key={trip.id}
                      trip={trip}
                      isSelected={selectedTripId === trip.id}
                      onSelect={() => handleSelectTrip(trip.id)}
                      badgePosition="left"
                      trailing={<div className="grid grid-cols-2 gap-0.5">{actionGrid}</div>}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <div className="space-y-0">
          <MenuRow
            icon={faPlus}
            tone="secondary"
            compact
            onClick={() => setShowNewTripForm(true)}
            className="py-1"
          >
            New trip
          </MenuRow>
          <MenuRow
            icon={faArrowUpFromLine}
            compact
            onClick={() => importInputRef.current?.click()}
            className="py-1"
          >
            Import trip
          </MenuRow>
          {showInstallRow && (
            <MenuRow icon={faMobileNotch} compact onClick={handleInstallClick} className="py-1">
              Add to home screen
            </MenuRow>
          )}
        </div>
      </div>
      {/* Both share-related modals (Collaborate, Collaborators) live
          at the App level — see App.tsx. Mounting them here would
          unmount them whenever the trip-selector popover closes (on
          first click inside the modal, since the popover treats that
          as an "outside" click). We dispatch window events from the
          button handlers above; App owns the modal state + render. */}
    </>
  );
};
